import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as ENV from "../Util/env";
import LocationDropdownOptions from "./LocationDropdownOptions";
import { topicDictionary } from "../Util/tileDictionary";
import {
  selectComparisonAllLocations,
  selectComparisonByView,
} from "../Util/Analytics/InteractionAnalytics";

/**
 * ComparisonSelector
 *
 * Purpose: Lightweight controls for choosing a comparison target.
 *
 * Comparison “views” (used by QuestionHeader):
 * - View 1: All Locations + 1 Question
 *   - Mode: year-only comparison across ALL locations (mode='allLocations').
 *   - UI: Year select + Compare (no location select).
 * - View 2: One Location + All Questions
 *   - Mode: location + year comparison (mode='questionAndLocation', showYear=true).
 *   - UI: Location select + Year select + Compare; typically shown after clicking “+ Compare…”.
 * - View 3: One Location + 1 Question (focused subquestion)
 *   - Mode: location-only comparison (mode='questionAndLocation', showYear=false).
 *   - UI: Label “Comparison Location”, Location select + Compare (year defaults to the current one).
 *
 * Table.js scenarios for context (rendered elsewhere):
 * 1) No subquestions, ALL locations → render ALL states table.
 * 2) With subquestions, ALL locations → render ALL states grouped tables.
 * 3) Specific location, subquestion list → render subquestions for that location.
 * 4) Patterns over time available → render POT table.
 * 5) No data message (patterns or non-patterns path) → render fallback text.
 *
 * Use the `variant` prop to assign a unique modifier class so styles can be
 * scoped per screen without overloading a single selector.
 */
// Lightweight comparison selector: allows choosing a second location + year.
// Props:
// - locations: array of location objects (LocationAbbr, LocationDesc)
// - questionId, subQuestionId: used to fetch available years for the selected comparison location
// - onCompare: callback({ locationAbbr, year }) invoked on Compare button
// - onCancel: hides the panel
// - excludeLocationAbbr: current primary location to exclude from list
const ComparisonSelector = ({
  locations,
  questionId,
  subQuestionId,
  year,
  onCompare,
  onCancel,
  excludeLocationAbbr,
  showCancel = true,
  // selectedData from parent allows us to derive the question/topic color
  selectedData,
  // mode controls what is rendered:
  // 'allLocations' => show year dropdown only (label: Comparison Year)
  // 'questionAndLocation' => show location dropdown and optionally year
  mode = "questionAndLocation",
  // variant controls the root BEM modifier class for screen-specific styles
  // Accepts: 'question-header' | 'all-locations' | 'table'
  variant = "question-header",
  // When mode='questionAndLocation', you can hide the year control (View 3)
  // to create a location-only compare where current year is reused.
  showYear = true,
  // Text matching props for Years API
  subQuestionText = "",
  summaryText = "",
}) => {
  const [cmpLocation, setCmpLocation] = useState("Nationwide");
  const [displayYear, setDisplayYear] = useState(year);
  const [yearOptions, setYearOptions] = useState([]);
  const [loadingYears, setLoadingYears] = useState(false);
  const [compareYearAvailable, setCompareYearAvailable] = useState(true);
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const question = selectedData ? selectedData.question : null;
  const topicColor =
    (question?.TopicId && topicDictionary[question.TopicId]?.color) || undefined;

  const baseLocationAbbr = selectedData?.location?.LocationAbbr || excludeLocationAbbr;

  const getBaseLocationFromUrl = () => {
    try {
      const hash = window.location && window.location.hash ? window.location.hash : "";
      const queryIndex = hash.indexOf("?");
      if (queryIndex === -1) return "";
      const params = new URLSearchParams(hash.slice(queryIndex + 1));
      return params.get("LocationAbbr") || "";
    } catch (e) {
      return "";
    }
  };

  const getBaseYearFromDropdown = useCallback(() => {
    const propYear = Number(year);
    if (Number.isFinite(propYear)) return propYear;
    try {
      const dropdown = document.getElementById("current-year-dropdown");
      const raw = dropdown && dropdown.value ? Number(dropdown.value) : NaN;
      return Number.isFinite(raw) ? raw : undefined;
    } catch (e) {
      return undefined;
    }
  }, [year]);

  const effectiveBaseLocationAbbr = baseLocationAbbr || getBaseLocationFromUrl();

  const filteredLocations = useMemo(() => {
    const list = (locations || []).slice();
    return list.filter(
      (l) =>
        l &&
        l.LocationType !== "All" &&
        l.LocationDesc !== "All Locations"
    );
  }, [locations]);

  function normalizeYears(data) {
    // Normalize to numeric years and drop non-finite entries
    return (data || [])
      .map((y) => (typeof y === "object" ? y.Year : y))
      .map((y) => (y === null || y === undefined ? NaN : Number(y)))
      .filter((y) => Number.isFinite(y));
  }

  function pickAlternateYear(current, options) {
    if (!options || options.length === 0) return undefined;
    const sorted = [...options].sort((a, b) => b - a); // descending (e.g., 2024, 2022)
    const currentNum = Number(current);
    if (!Number.isFinite(currentNum)) {
      // If current is not a finite number, pick the most recent option
      return sorted[0];
    }
    const idx = sorted.indexOf(currentNum);
    if (idx >= 0) {
      // Prefer the next LOWER year (later in the descending array)
      if (idx < sorted.length - 1) return sorted[idx + 1];
      // Else fall back to the next HIGHER year (earlier in array)
      if (idx > 0) return sorted[idx - 1];
      // Only one option
      return sorted[0];
    }
    // Current not in options: choose the greatest option strictly less than current
    const nextLower = sorted.find((y) => y < currentNum);
    if (Number.isFinite(nextLower)) return nextLower;
    // No lower option; fallback to most recent
    return sorted[0];
  }

  // Resolve initial location object for default value
  useEffect(() => {
    if (!filteredLocations || !filteredLocations.length) return;
    const allowed = filteredLocations;
    const nationwide = allowed.find(
      (l) => l.LocationDesc === "Nationwide" || l.LocationAbbr === "US" || l.LocationAbbr === "XX"
    );
    if (nationwide) {
      setCmpLocation(nationwide.LocationAbbr);
    } else if (allowed.length) {
      setCmpLocation(allowed[0].LocationAbbr);
    }
  }, [filteredLocations]);

  // Fetch year options for ALL Locations mode
  useEffect(() => {
    if (mode !== "allLocations" || !questionId) return;
    setLoadingYears(true);
    // Use text matching when EnableSubQuestionTextMatching is true: prefer subQuestionText, fall back to summaryText only when empty
    const params = new URLSearchParams();
    params.append("QuestionId", questionId);
    params.append("LocationAbbr", "ALL");
    const textToMatch = (subQuestionText && subQuestionText.trim()) ? subQuestionText : (summaryText && summaryText.trim()) ? summaryText : "";
    if (ENV.EnableSubQuestionTextMatching && textToMatch) {
      params.append("MatchByText", "true");
      if (subQuestionText && subQuestionText.trim()) {
        params.append("SubQuestionText", subQuestionText);
      } else if (summaryText && summaryText.trim()) {
        params.append("SummaryText", summaryText);
      }
    }
    const url = `${ENV.APIBaseURL}/Years?${params.toString()}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const yrs = normalizeYears(data);
        setYearOptions(yrs);
        if (yrs.length) setDisplayYear(pickAlternateYear(year, yrs));
      })
      .finally(() => setLoadingYears(false));
  }, [mode, questionId, subQuestionId, year, subQuestionText, summaryText]);

  // Fetch year options for selected comparison location in questionAndLocation mode
  useEffect(() => {
    if (mode !== "questionAndLocation" || !questionId || !cmpLocation) return;

    setLoadingYears(true);
    setCompareYearAvailable(true);
    // Use text matching when EnableSubQuestionTextMatching is true: prefer subQuestionText, fall back to summaryText only when empty
    const params = new URLSearchParams();
    params.append("QuestionId", questionId);
    params.append("LocationAbbr", cmpLocation);
    const textToMatch = (subQuestionText && subQuestionText.trim()) ? subQuestionText : (summaryText && summaryText.trim()) ? summaryText : "";
    if (ENV.EnableSubQuestionTextMatching && textToMatch) {
      params.append("MatchByText", "true");
      if (subQuestionText && subQuestionText.trim()) {
        params.append("SubQuestionText", subQuestionText);
      } else if (summaryText && summaryText.trim()) {
        params.append("SummaryText", summaryText);
      }
    }
    const url = `${ENV.APIBaseURL}/Years?${params.toString()}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const normalized = normalizeYears(data);
        const isNationwideLocation =
          cmpLocation === "Nationwide" ||
          cmpLocation === "US" ||
          cmpLocation === "XX";
        const yrs =
          isNationwideLocation && (!normalized || normalized.length === 0)
            ? [2024, 2022]
            : normalized;
        const baseYear = getBaseYearFromDropdown();
        const hasBaseYear = Number.isFinite(baseYear) && yrs.includes(baseYear);
        const isSameLocation = cmpLocation === effectiveBaseLocationAbbr;
        setYearOptions(yrs);
        setCompareYearAvailable(isSameLocation ? hasBaseYear : yrs.length > 0);

        if (!isSameLocation) {
          if (hasBaseYear) {
            setDisplayYear(baseYear);
          } else {
            setDisplayYear(yrs.length > 0 ? pickAlternateYear(baseYear, yrs) : undefined);
          }
          return;
        }

        const altOptions = yrs.filter((y) => Number(y) !== baseYear);
        setDisplayYear(altOptions.length ? pickAlternateYear(baseYear, altOptions) : undefined);
      })
      .catch(() => {
        setYearOptions([]);
        setDisplayYear(undefined);
        setCompareYearAvailable(false);
      })
      .finally(() => setLoadingYears(false));
  }, [mode, questionId, subQuestionId, cmpLocation, year, effectiveBaseLocationAbbr, subQuestionText, summaryText, getBaseYearFromDropdown]);

  // If parent base year changes and we already have options, recompute the alternate immediately
  useEffect(() => {
    if (!showYear) return;
    if (cmpLocation !== effectiveBaseLocationAbbr) return;
    if (yearOptions && yearOptions.length) {
      const baseYear = getBaseYearFromDropdown();
      const altOptions = yearOptions.filter((y) => Number(y) !== baseYear);
      const alt = altOptions.length ? pickAlternateYear(baseYear, altOptions) : undefined;
      if (Number(alt) !== Number(displayYear)) {
        setDisplayYear(alt);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, showYear, yearOptions, cmpLocation, effectiveBaseLocationAbbr]);

  // locationOptions now derived via useMemo above

  const renderContent = () => {
    const baseYear = getBaseYearFromDropdown();
    const isSameLocation = cmpLocation === effectiveBaseLocationAbbr;
    const hasAltYear = !showYear || !isSameLocation || yearOptions.some((y) => Number(y) !== Number(baseYear));
    const compareAvailable = !showYear ? true : (isSameLocation ? hasAltYear : compareYearAvailable);
    const isNationwideCmp =
      cmpLocation === "Nationwide" ||
      cmpLocation === "US" ||
      cmpLocation === "XX";

    if (mode === "allLocations") {
      return (
        <>
          <div className="comparison-selector__controls">
            <span className="comparison-selector__label">Comparison Year</span>
            <select
              aria-label="Comparison year"
              id="comparison-year-dropdown"
              className="selectpicker form-control year-dropdown"
              value={displayYear || ""}
              disabled={loadingYears}
              onChange={(e) => setDisplayYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <button
              id="compare-button-view1"
              className="btn  btn-sm"
              disabled={!displayYear || Number(displayYear) === Number(year)}
              onClick={() => {
                selectComparisonAllLocations();
                onCompare && onCompare({ locationAbbr: "ALL", year: displayYear });
              }}
               style={topicColor ? { backgroundColor: topicColor, borderColor: topicColor, color: '#fff' } : undefined}
            >
              Submit
            </button>
            {showCancel && (
              <button
                type="button"
                aria-label="Cancel comparison"
                className="btn btn-link btn-sm p-0 comparison-selector__cancel"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </>
      );
    }
    // Default: questionAndLocation
    return (
      <>
        <span className="comparison-selector__label">{showYear ? "Comparison Location/Year" : "Comparison Location"}</span>
        <div className="comparison-selector__controls">
          <div className="location-filter-wrapper" style={{ paddingTop: 0, position: "relative", marginRight: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                tabIndex="0"
                className="selectpicker form-control"
                onClick={() => setShowLocationMenu((v) => !v)}
                onKeyDown={(e) => e.code === "Enter" && setShowLocationMenu((v) => !v)}
                style={{ position: "relative", zIndex: 1001, minWidth: 140 }}
                role="combobox"
                aria-label="comparison-select-location"
                aria-expanded={showLocationMenu ? "true" : "false"}
                aria-controls="comparison-location-dropdown-menu"
              >
                <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(filteredLocations.find((l) => l.LocationAbbr === cmpLocation) || {}).LocationDesc || "Select a Location"}
                  </span>
                  <span>▾</span>
                </span>
              </div>
            </div>
            <div
              className="location-dropdown"
              id="comparison-location-dropdown-menu"
              style={!showLocationMenu ? { display: "none" } : { display: "block" }}
            >
              {filteredLocations && (
                <LocationDropdownOptions
                  displayLocations={showLocationMenu}
                  displayLocationDropdown={() => setShowLocationMenu(false)}
                  // For View #3 (showYear=false), exclude the base location to prevent comparing Alaska to Alaska
                  locations={!showYear ? filteredLocations.filter((l) => l.LocationAbbr !== baseLocationAbbr) : filteredLocations}
                  trackLocationInteraction={false}
                  onLocationChange={(loc) => {
                    // Adapt homepage dropdown behavior to set comparison target only
                    setCmpLocation(loc.LocationAbbr);
                    setShowLocationMenu(false);
                  }}
                  setLocationSelectedFromDropdownMenu={() => {}}
                />
              )}
            </div>
          </div>
          {showYear && (
            <select
              aria-label="Comparison year"
              id="comparison-year-dropdown"
              className="selectpicker form-control year-dropdown"
              value={displayYear || ""}
              disabled={loadingYears || !compareAvailable}
              onChange={(e) => setDisplayYear(Number(e.target.value))}
            >
              {(!isNationwideCmp || !compareAvailable) && (
                <option value="" disabled>
                  {compareAvailable ? "Year" : "N/A"}
                </option>
              )}
              {/* When same location is selected, exclude the current year to prevent comparing Alaska 2024 to Alaska 2024 */}
              {yearOptions
                .filter((y) => !isSameLocation || Number(y) !== Number(year))
                .map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
            </select>
          )}
          <button
            id="compare-button-view2"
            className="btn text-white btn-sm"
            disabled={
              !cmpLocation || 
              loadingYears || 
              !compareAvailable ||
              (showYear && !displayYear) ||
              // Prevent comparing same location + same year (View #2)
              (isSameLocation && showYear && Number(displayYear) === Number(year)) ||
              // Prevent comparing same location (View #3: showYear=false)
              (!showYear && isSameLocation)
            }
            onClick={() => {
              selectComparisonByView({ showYear });
              onCompare && onCompare({ locationAbbr: cmpLocation, year: showYear ? displayYear : year });
            }}
            style={{ backgroundColor: topicDictionary[question.TopicId].color }}
          >
            Submit
          </button>
          {showCancel && (
            <button
              type="button"
              aria-label="Cancel comparison"
              className="btn btn-link btn-sm p-0 comparison-selector__cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </>
    );
  };

  // Apply variant modifier so styles can target the screen context precisely
  const rootClass = [
    "comparison-selector",
    variant ? `comparison-selector--${variant}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className={rootClass} data-variant={variant}>
        {renderContent()}
      </div>
    </>
  );
};

export default ComparisonSelector;
